import { useEffect, useMemo, useRef, useState } from "react";
import { MapPin, Search, Loader2, Locate, X } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * CitySelector — used in the header + record-entry forms.
 * onChange gets the city NAME (string), or null when cleared.
 */
export default function CitySelector({ value, onChange, compact = false, testid = "city-selector" }) {
  const { refreshMe } = useAuth();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [locating, setLocating] = useState(false);
  const anchorRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get(`/cities/search?q=${encodeURIComponent(query)}`)
      .then((r) => setCities(r.data.cities || []))
      .finally(() => setLoading(false));
  }, [open, query]);

  useEffect(() => {
    if (!open) return;
    const close = (e) => {
      if (anchorRef.current && !anchorRef.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener("mousedown", close);
    return () => window.removeEventListener("mousedown", close);
  }, [open]);

  const pick = async (cityName) => {
    setOpen(false);
    setQuery("");
    if (typeof onChange === "function") onChange(cityName);
    try {
      await api.patch("/user/preferences", { preferred_city: cityName });
      await refreshMe();
    } catch { /* handled elsewhere */ }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const r = await api.get(`/cities/reverse-geocode?lat=${pos.coords.latitude}&lng=${pos.coords.longitude}`);
          if (r.data?.city?.name) await pick(r.data.city.name);
        } finally {
          setLocating(false);
        }
      },
      () => setLocating(false),
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 60_000 }
    );
  };

  const label = value || "Set city";

  return (
    <div className="relative" ref={anchorRef}>
      <button
        data-testid={testid}
        onClick={() => setOpen((o) => !o)}
        className={
          "inline-flex items-center gap-1.5 " +
          (compact
            ? "px-2 py-1 rounded-md border border-[var(--border-line)] text-xs bg-[var(--bg-surface)] hover:bg-[var(--hover)]"
            : "px-3 py-2 rounded-lg border border-[var(--border-line)] text-sm bg-[var(--bg-surface)] hover:bg-[var(--hover)]")
        }
      >
        <MapPin size={14} className="text-[#FF5A1F]" />
        <span className="max-w-[110px] truncate">{label}</span>
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-[280px] rounded-xl border border-[var(--border-line)] bg-[var(--bg-surface)] shadow-xl z-50 p-2"
          data-testid="city-selector-panel"
        >
          <div className="flex items-center gap-2 border-b border-[var(--border-line)] pb-2 mb-2">
            <Search size={14} className="text-[#A3A3A3]" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search city or state…"
              className="bg-transparent flex-1 outline-none text-sm"
              data-testid="city-search-input"
            />
            {query && (
              <button onClick={() => setQuery("")} className="text-[var(--text-tertiary)] hover:text-[var(--text-primary)]">
                <X size={14} />
              </button>
            )}
          </div>
          <button
            onClick={useMyLocation}
            disabled={locating}
            className="w-full text-left flex items-center gap-2 px-3 py-2 rounded-md text-sm hover:bg-[var(--hover)] text-[#FF5A1F]"
            data-testid="city-use-location-btn"
          >
            {locating ? <Loader2 size={14} className="animate-spin" /> : <Locate size={14} />}
            Use my location
          </button>
          <div className="max-h-[260px] overflow-y-auto mt-1">
            {loading ? (
              <div className="p-3 text-xs text-[#737373] flex items-center gap-2">
                <Loader2 size={12} className="animate-spin" /> Loading…
              </div>
            ) : cities.length === 0 ? (
              <div className="p-3 text-xs text-[#737373]">No cities found.</div>
            ) : (
              cities.map((c) => (
                <button
                  key={c.name}
                  onClick={() => pick(c.name)}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm hover:bg-[var(--hover)] flex items-center justify-between ${value === c.name ? "text-[#FF5A1F]" : "text-[var(--text-primary)]"}`}
                  data-testid={`city-option-${c.name.toLowerCase().replace(/\s+/g,'-')}`}
                >
                  <span className="truncate">{c.name}</span>
                  <span className="text-[10px] text-[var(--text-tertiary)]">{c.state}</span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
